/**
 * <s-cart> manages all cart quantity updates and removals via the command pattern.
 * It fetches partials after each mutation so the cart drawer, cart page, and header
 * counters all stay in sync — do not convert to a full page reload or direct DOM manipulation.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --update-quantity — Adjusts quantity by [data-delta] for [data-variant-id]; debounced 200ms.
 *   --remove          — Removes the line for [data-variant-id] (sets quantity to 0).
 *   --apply-discount  — Applies the value of #cart-discount-input as a discount code.
 *
 * Required child markup (queried by selector/attribute):
 *   [data-variant-id]                                — Identifies which cart line each command targets in the DOM.
 *   [data-line-key]                                  — Cart line key ({{ item.key }}); sent to updateCart as the line id.
 *   [data-delta]                                     — Integer delta (±N) paired with --update-quantity.
 *   [data-qty-min]                                   — Minimum quantity from variant.quantity_rule.min (default 1).
 *   [data-qty-max]                                   — Maximum quantity from variant.quantity_rule.max (when absent, no max).
 *   [data-qty-step]                                  — Increment step from variant.quantity_rule.increment (default 1).
 *   [data-quantity-display][data-variant-id]         — Input/element holding the current quantity (value or textContent).
 *   [style*="view-transition-name"]                  — Row wrapper used for removal focus-restore + view transitions.
 *
 * Element dataset (attributes on <s-cart> itself):
 *   data-partials — Comma-separated partial names to re-fetch (default: "cart-items,cart-count").
 *                   Must match {% partial 'name' %} blocks in cart.liquid / theme.liquid.
 *
 * External coupling:
 *   Cart success/error feedback (#sr-announcer + #toast-region) is owned by the
 *   updateCart handler in standard-actions-override.js — do not duplicate here.
 */
import { partials } from '@shopify/partial-rendering';
import { transition } from 'view-transition';
import { normalizeQuantity, quantityRulesFromDataset } from './quantity-rules.js';
import { ViewEventElement } from './s-view-event.js';

class CartItems extends ViewEventElement {
  #lifecycleAc;
  #quantityAc;
  #discountAc;
  #noteAc;
  #rafId;
  #debounceTimers = new Map();
  #rollbackValues = new Map();
  #openDetails = new Set();

  static async refreshMountedCarts({ signal } = {}) {
    const carts = [...document.querySelectorAll('s-cart')].filter((cart) => cart instanceof CartItems);
    const partialNames = [...new Set(carts.flatMap((cart) => cart.#partialNames))];

    if (!partialNames.length) return;

    // Capture UI state before partial rendering replaces the DOM
    for (const cart of carts) cart.#saveTransientState();

    const html = await partials.fetch(...partialNames, { signal });
    signal?.throwIfAborted();

    await transition(() => partials.apply(html));
    signal?.throwIfAborted();

    for (const cart of carts) {
      cart.#reconcileAfterRefresh();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.#lifecycleAc = new AbortController();
    const opts = { signal: this.#lifecycleAc.signal };

    this.addEventListener('command', (e) => {
      const variantId = e.source.dataset.variantId;

      if (e.command === '--update-quantity') {
        const display = this.querySelector(
          `[data-quantity-display][data-variant-id="${variantId}"]`
        );
        if (!display) return;
        const current = parseInt(display.value ?? display.textContent, 10) || 0;
        const delta = parseInt(e.source.dataset.delta, 10) || 0;
        const newQuantity = normalizeQuantity(
          current + delta,
          quantityRulesFromDataset(e.source),
          { allowZero: true }
        );

        display.value = newQuantity;
        const triggerButton = e.source;
        const focusSelector = triggerButton
          ? `button[data-variant-id="${variantId}"][data-delta="${triggerButton.dataset.delta}"]`
          : null;
        const lineKey = e.source.dataset.lineKey;
        if (!this.#debounceTimers.has(variantId)) this.#rollbackValues.set(variantId, current);
        clearTimeout(this.#debounceTimers.get(variantId));
        const rollbackValue = this.#rollbackValues.get(variantId);
        this.#debounceTimers.set(variantId, setTimeout(() => {
          this.#debounceTimers.delete(variantId);
          this.#rollbackValues.delete(variantId);
          this.#updateCart(variantId, newQuantity, focusSelector, () => {
            const el = this.querySelector(`[data-quantity-display][data-variant-id="${variantId}"]`);
            if (el) el.value = rollbackValue;
          }, lineKey);
        }, 200));
      }

      if (e.command === '--remove') {
        this.#updateCart(variantId, 0, null, null, e.source.dataset.lineKey);
      }

      if (e.command === '--apply-discount') {
        this.#applyDiscount(this.querySelector('#cart-discount-input')?.value);
      }
    }, opts);

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.id === 'cart-discount-input') {
        e.preventDefault();
        this.#applyDiscount(e.target.value);
      }
    }, opts);

    this.addEventListener('change', (e) => {
      const input = e.target;

      if (input.matches('[data-quantity-display]')) {
        const variantId = input.dataset.variantId;
        clearTimeout(this.#debounceTimers.get(variantId));
        this.#debounceTimers.delete(variantId);
        this.#rollbackValues.delete(variantId);
        const parsed = parseInt(input.value, 10);
        const quantity = normalizeQuantity(
          parsed,
          quantityRulesFromDataset(input),
          { allowZero: true }
        );

        input.value = quantity;
        const focusSelector = `[data-quantity-display][data-variant-id="${variantId}"]`;
        this.#updateCart(variantId, quantity, focusSelector, null, input.dataset.lineKey);
      } else if (input.id === 'cart-note') {
        this.#updateNote(input.value);
      }
    }, opts);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#lifecycleAc?.abort();
    this.#quantityAc?.abort();
    this.#discountAc?.abort();
    this.#noteAc?.abort();
    for (const timer of this.#debounceTimers.values()) clearTimeout(timer);
    this.#debounceTimers.clear();
    this.#rollbackValues.clear();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  #findCartRow(variantId) {
    return this.querySelector(`[data-variant-id="${variantId}"]`)
      ?.closest('[style*="view-transition-name"]');
  }

  get #partialNames() {
    // dataset.partials mirrors the Liquid-written data-partials="..." attribute.
    // The fallback names ('cart-items', 'cart-count') must stay in sync with the
    // {% partial %} block names in cart.liquid and theme.liquid — renaming either breaks cart updates.
    const names = this.dataset.partials;
    return names ? names.split(',').map((s) => s.trim()) : ['cart-items', 'cart-count'];
  }

  #saveTransientState() {
    this.#openDetails.clear();
    for (const details of this.querySelectorAll('details[open]')) {
      const id = details.querySelector('summary')?.textContent.trim();
      if (id) this.#openDetails.add(id);
    }
  }

  #reconcileAfterRefresh() {
    for (const input of this.querySelectorAll('[data-quantity-display]')) {
      if ((input instanceof HTMLInputElement)) {
        // partials.apply() preserves form control state, but cart quantities are
        // server-owned after a cart refresh and should reflect the new markup.
        input.value = input.defaultValue;
      }
    }

    // Restore <details> open state lost during partial rendering
    for (const details of this.querySelectorAll('details')) {
      const id = details.querySelector('summary')?.textContent.trim();
      if (id && this.#openDetails.has(id)) details.open = true;
    }
    this.#openDetails.clear();
  }

  async #updateCart(variantId, quantity, focusSelector = null, onRollback = null, lineKey = null) {
    // Abort stale mutation so rapid +/- clicks don't leave an earlier quantity change racing a newer one
    this.#quantityAc?.abort();
    this.#quantityAc = new AbortController();
    const { signal } = this.#quantityAc;

    this.setAttribute('aria-busy', 'true');

    const isRemoval = quantity === 0;
    const row = isRemoval ? this.#findCartRow(variantId) : null;
    const sibling = row?.nextElementSibling || row?.previousElementSibling;
    const siblingVariantId = sibling?.querySelector('[data-variant-id]')?.dataset.variantId;

    try {
      // Cart mutation goes through Standard Actions (Storefront API). The shared
      // updateCart override in standard-actions-override.js triggers a partial refresh
      // (cart-items, cart-count, etc.) via CartItems.refreshMountedCarts() after the
      // mutation resolves. The {% partial 'name' %} blocks in cart.liquid and
      // theme.liquid remain the source of truth for what gets re-rendered.
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ id: lineKey, quantity }] },
        {
          event: { context: 'cart' },
          signal,
        }
      );

      const userError = result?.userErrors?.[0]?.message;
      if (userError) {
        throw new Error(userError, {
          cause: { userErrors: result.userErrors },
        });
      }

      if (signal.aborted) return;
      this.removeAttribute('aria-busy');

      // Success announcement (#sr-announcer + toast) is owned by
      // standard-actions-override. Don't write to [data-cart-status] here —
      // both are aria-live="polite" and would announce twice.

      if (isRemoval) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = requestAnimationFrame(() => {
          const freshRow = siblingVariantId ? this.#findCartRow(siblingVariantId) : null;
          const focusTarget = freshRow?.querySelector('a, button')
            || this.querySelector('a, button')
            || this.closest('main');
          focusTarget?.focus();
          this.#rafId = null;
        });
      } else if (focusSelector) {
        cancelAnimationFrame(this.#rafId);
        this.#rafId = requestAnimationFrame(() => {
          this.querySelector(focusSelector)?.focus();
          this.#rafId = null;
        });
      }
    } catch (error) {
      if (signal.aborted) return;
      onRollback?.();
      this.removeAttribute('aria-busy');
      // Cart error feedback (toast + #sr-announcer text) is owned by the
      // standard-actions-override updateCart handler. Don't duplicate the
      // announcement on [data-cart-status] here — it would announce the same
      // failure 2-3x to assistive tech.
    }
  }

  async #applyDiscount(code) {
    code = code?.trim();
    if (!code) return;

    this.#discountAc?.abort();
    this.#discountAc = new AbortController();
    const { signal } = this.#discountAc;

    this.setAttribute('aria-busy', 'true');
    try {
      const result = await window.Shopify.actions.updateCart(
        { discountCodes: [code] },
        { event: { context: 'cart' }, signal }
      );
      const userError = result?.userErrors?.[0]?.message;
      if (userError) throw new Error(userError, { cause: { userErrors: result.userErrors } });
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

  async #updateNote(note) {
    this.#noteAc?.abort();
    this.#noteAc = new AbortController();
    const { signal } = this.#noteAc;

    this.setAttribute('aria-busy', 'true');
    try {
      await window.Shopify.actions.updateCart(
        { note },
        { event: { context: 'cart' }, signal }
      );
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (signal.aborted) return;
      this.removeAttribute('aria-busy');
    }
  }

}

if (!customElements.get('s-cart')) {
  customElements.define('s-cart', CartItems);
}
